import {
  HostListener,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import {
  debounceTime,
  Subscription,
} from 'rxjs';
import { IonInput } from '@ionic/angular';
import { DeviceDetectorService } from 'ngx-device-detector';

import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { FrontendDropdownService } from '../shared/frontend-dropdown.service';

@Component({
  selector: 'frontend-dropdown-plain',
  styleUrls: ['../shared/frontend-dropdown.scss'],
  templateUrl: './frontend-dropdown-plain.component.html',
  providers: [
    FrontendDropdownService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FrontendDropdownPlainComponent),
      multi: true,
    },
  ],
})
export class FrontendDropdownPlainComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @ViewChild('modalContent') modalContentRef?: ElementRef;
  @ViewChild('searchField') searchField?: IonInput;
  @ViewChild('sectionRef') sectionRef?: ElementRef;
  @ViewChild('listContent') listContent?: ElementRef;

  @Output() itemClear = new EventEmitter<string>();
  @Output() itemsClear = new EventEmitter<void>();
  @Output() popoverOpen = new EventEmitter<void>();
  @Output() popoverClose = new EventEmitter<void>();
  @Output() searchTermChange = new EventEmitter<string>();

  @Input() showAddTagOnlyIfNoResults = false;
  @Input() searchable = true;
  @Input() disableFiltering = false;
  @Input() customName?: (item: string) => string;
  @Input() customOptionHtml?: (item: string) => string;
  @Input() virtualScroll = false;
  @Input() customClass = '';

  @Input() items: string[] = [];
  @Input() maxSelectedItems?: number;
  @Input() clearAllText = 'Clear';
  @Input() elementId = 'default-dropdown-id';
  @Input() noItemsText = 'No items';
  @Input() multiple = false;
  @Input() placeholder = '';
  @Input() addTag = false;
  @Input() titleCase = false;
  @Input() clearable = false;
  @Input() minWidth = '6rem';
  @Input() maxListHeight = '11rem';

  icon = AppIcon;
  iconSize = AppIconSize;
  searchString = '';
  displayItems: string[] = [];
  isPopoverOpen = false;
  inputCanBeFocused = false;

  itemsIdToStateMap: Record<string, boolean> = {};

  onChange?: (value: string[] | string) => void;
  onTouched?: () => void;
  value: string[] = [];
  disabled = false;
  hideInput = false;

  popoverLeft = 0;
  popoverTop = 0;
  popoverWidth = 0;
  popoverInverted = false;

  private readonly subscription = new Subscription();

  constructor(
    readonly service: FrontendDropdownService,
    private readonly cdr: ChangeDetectorRef,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly deviceDetectorService: DeviceDetectorService,
  ) { }

  @HostListener('document:keydown.enter', ['$event'])
  onClick(): void {
    if (this.searchString?.length > 0) {
      if (this.addTag && !this.items.includes(this.searchString)) {
        this.addNewItem();

        return;
      }

      if (this.displayItems.length > 0) {
        const item = this.displayItems[0];
        if (this.multiple) {
          this.checkboxChange(item, { checked: true });
        } else {
          this.selectSingleItem(item);
        }
      }

      this.searchString = '';
    }
  }

  presentPopover(): void {
    this.inputCanBeFocused = true;

    const deviceType = this.deviceDetectorService.deviceType;
    if (deviceType === 'tablet' || deviceType === 'mobile') {
      void this.sectionRef?.nativeElement.scrollIntoView({ block: 'start' });
    }

    void this.searchField?.setFocus();
    this.cdr.detectChanges();
    this.isPopoverOpen = true;
    this.popoverOpen.emit();
  }

  clickOutside(): void {
    this.isPopoverOpen = false;
  }

  unfocusSearchInput(): void {
    this.hideInput = true;
    this.inputCanBeFocused = false;

    setTimeout(() => {
      this.hideInput = false;
      this.cdr.detectChanges();
    }, 300);
  }

  keySearch = (key: KeyboardEvent): void => {
    if (this.multiple) {
      return;
    }

    this.service.keySearchInArray(key, this.displayItems, this.listContent?.nativeElement);
  };

  clearSearchTermListener = (click: MouseEvent | TouchEvent): void => {
    const target = click.target as HTMLElement;
    if (!this.sectionRef?.nativeElement.contains(target)) {
      this.searchString = '';
      this.filterDisplayItems();
    }
  };

  viewResize = (resize: UIEvent): void => {
    // TODO: implement later
  };

  ngOnInit(): void {
    window.addEventListener('resize', this.viewResize);
    document.addEventListener('keyup', this.keySearch);
    document.addEventListener('click', this.clearSearchTermListener);

    const orientationSub = this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      Breakpoints.TabletPortrait,
      Breakpoints.TabletLandscape,
      Breakpoints.WebPortrait,
      Breakpoints.WebLandscape,
    ])
      .subscribe(() => {
        this.isPopoverOpen = false;
        this.unfocusSearchInput();
      });
    this.subscription.add(orientationSub);

    const keyboardSub = this.service.viewportResizeFinish$
      .pipe(debounceTime(100))
      .subscribe((viewportHeight) => {
        if (!this.isPopoverOpen) {
          return;
        }

        if (this.getDropdownHeight().lowestPoint < viewportHeight) {
          return;
        }

        // dropdown doesnt fit into the view...
        this.calculatePositionInternalSync();
      });
    this.subscription.add(keyboardSub);
  }

  getDropdownHeight(): { lowestPoint: number; dropdownHeight: number } {
    const modalContent = document.getElementsByClassName('popover-modal-content');
    const sectionRect = this.sectionRef?.nativeElement.getBoundingClientRect();
    let dropdownHeight = 0;

    if (modalContent?.length > 0) {
      dropdownHeight += (modalContent.item(0) as HTMLElement).offsetHeight;
    }

    return {
      lowestPoint: sectionRect.top + sectionRect.height + dropdownHeight,
      dropdownHeight,
    };
  }

  calculatePositionInternalSync(): void {
    if (!this.sectionRef) {
      return;
    }

    const rect = this.sectionRef?.nativeElement.getBoundingClientRect();
    this.popoverLeft = rect.left + window.scrollX - 8;
    this.popoverTop = 0;
    this.popoverWidth = rect.width;
    this.popoverInverted = false;
    this.cdr.detectChanges();

    setTimeout(() => {
      const { lowestPoint, dropdownHeight } = this.getDropdownHeight();
      this.popoverInverted = window.innerHeight < lowestPoint;

      this.popoverTop = this.popoverInverted
        ? rect.top + window.scrollY - dropdownHeight
        : rect.top + window.scrollY + rect.height;
      this.cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['items'] && this.items && Object.keys(this.items).length)) {
      this.generateMaps();
      this.filterDisplayItems();
    }
  }

  clearAll(): void {
    if (!this.clearable) {
      return;
    }

    this.isPopoverOpen = false;
    if (!this.multiple) {
      this.selectSingleItem('');
      this.value = [];
    }

    if (this.multiple) {
      this.value = [];
      this.checkboxChangeInternal();
    }

    this.generateMaps();
    this.filterDisplayItems();
  }

  resetItems(): void {
    this.items = [];
    this.generateMaps();
    this.filterDisplayItems();
  }

  generateMaps(): void {
    this.itemsIdToStateMap = {};

    if (!this.items || !this.value) {
      return;
    }

    this.items.forEach((item) => {
      this.itemsIdToStateMap[item] = this.value.includes(item);
    });
  }

  ngOnDestroy(): void {
    const leftoverModal = document.getElementById(`dropdown-modal-${this.elementId}`);
    if (leftoverModal) {
      leftoverModal.remove();
    }

    this.isPopoverOpen = false;
    window.removeEventListener('resize', this.viewResize);
    document.removeEventListener('keyup', this.keySearch);
    document.removeEventListener('click', this.clearSearchTermListener);
    this.service.unsub();
  }

  addNewItem(): void {
    if (this.maxSelectedItems && this.maxSelectedItems <= this.value?.length) {
      this.searchString = '';
      return;
    }

    if (!this.items) {
      this.items = [];
    }

    if (this.items.includes(this.searchString)) {
      this.searchString = '';
      return;
    }

    if (this.multiple) {
      this.items = [...this.items, this.searchString];
      this.checkboxChange(this.searchString, { checked: true });
    } else {
      this.selectSingleItem(this.searchString);
    }

    this.searchString = '';
    this.filterDisplayItems();
    void this.searchField?.setFocus();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: string[] | string): void {
    this.value = typeof value === 'string' ? [value] : value;

    if (!value || value === '') {
      this.value = [];
    }

    if (this.value.length > 0) {
      const itemsToCombine: string[] = [];
      this.value.forEach((val) => {
        if (this.items.includes(val)) {
          return;
        }

        itemsToCombine.push(val);
      });

      this.items = [...this.items, ...itemsToCombine];
    }

    this.generateMaps();
    this.filterDisplayItems();
  }

  selectSingleItem(item: string): void {
    if (this.disabled) {
      return;
    }

    this.searchString = '';
    if (this.multiple) {
      return;
    }

    this.value = [item];
    if (this.onChange) {
      this.onChange(this.multiple ? [item] : item);
    }
    if (this.onTouched) {
      this.onTouched();
    }

    this.generateMaps();
    this.isPopoverOpen = false;
  }

  deleteLastItem(keyboard: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    if (
      !keyboard?.key ||
      keyboard.key.toLowerCase() !== 'backspace' ||
      !this.clearable ||
      this.searchString !== ''
    ) {
      return;
    }

    if (this.multiple && this.value.length > 0) {
      this.checkboxChange(this.value[this.value.length - 1], { checked: false });
    }

    if (!this.multiple) {
      this.value = [];
    }

    this.isPopoverOpen = false;
  }

  checkboxChange<D extends { checked: boolean }>(id: string, eventDetail: D): void {
    const isChecked = eventDetail?.checked;

    if (this.disabled) {
      return;
    }

    if (!this.value) {
      this.value = [];
    }

    if (isChecked && !this.value.includes(id)) {
      this.value = [...this.value, id];
      this.searchString = '';
    }

    if (!isChecked) {
      this.value = this.value.filter(item => item !== id);
    }

    this.checkboxChangeInternal();
  }

  filterDisplayItems(): void {
    if (this.searchString?.length > 0) {
      this.presentPopover();
    }

    if (!this.items) {
      return;
    }

    if (!this.multiple && this.value.length > 0) {
      this.searchString = '';
    }

    if (this.disableFiltering) {
      this.displayItems = this.items;
      return;
    }

    this.displayItems = this.items.filter((item) => {
      return item.toLowerCase().includes(this.searchString.toLowerCase());
    });
  }

  private checkboxChangeInternal(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
    if (this.onTouched) {
      this.onTouched();
    }

    this.generateMaps();
    this.isPopoverOpen = false;
  }

  protected readonly document = document;
}
